<template>
  <div class="metrics-student-progress-chart">
    <div class="metrics-student-progress-chart__header">
      <span class="metrics-student-progress-chart__title heading-s">
        {{ $t("metrics.progressAcrossAssignments") }}
      </span>

      <div v-if="hasData" class="metrics-student-progress-chart__legend">
        <div
          v-for="item in legend"
          :key="item.label"
          class="metrics-student-progress-chart__legend-item"
        >
          <span
            class="metrics-student-progress-chart__legend-line"
            :style="{ background: item.color }"
          />
          <span
            class="metrics-student-progress-chart__legend-label paragraph-s"
          >
            {{ item.label }}
          </span>
        </div>
      </div>
    </div>

    <div ref="chartBody" class="metrics-student-progress-chart__body">
      <infinity-loading v-if="isLoading && !hasData" />
      <chart-j-s-line
        v-else-if="hasData"
        :chart-data="builtChartData"
        :chart-options="chartOptions"
        :plugins="chartPlugins"
        width="100%"
        :height="260"
      />
      <metrics-empty-state v-else :description="emptyDescription" />

      <div
        v-show="tooltipState.visible"
        class="progress-chart-tooltip"
        :style="{
          left: tooltipState.left + 'px',
          top: tooltipState.top + 'px',
        }"
      >
        <p class="progress-chart-tooltip__title paragraph-s">
          {{ tooltipState.title }}
        </p>
        <div
          v-for="item in tooltipState.items"
          :key="item.label"
          class="progress-chart-tooltip__row"
        >
          <div class="progress-chart-tooltip__row-left">
            <span
              class="progress-chart-tooltip__line"
              :style="{ background: item.color }"
            />
            <span class="progress-chart-tooltip__label paragraph-s">
              {{ item.label }}
            </span>
          </div>
          <strong class="progress-chart-tooltip__value paragraph-s">
            {{ item.value }}%
          </strong>
        </div>
      </div>
    </div>

    <div
      v-if="rows.length > 0"
      class="metrics-student-progress-chart__table-wrap"
    >
      <eg-pv-table :items="rows" :fields="tableFields">
        <template #cell(date)="{ item }">
          <span class="metrics-student-progress-chart__date">
            {{ item.date }}
          </span>
        </template>
        <template #cell(studentScore)="{ item }">
          <span
            v-if="item.studentScore != null"
            class="metrics-student-progress-chart__score-badge"
            :class="scoreBadgeClass(item.studentScore)"
          >
            {{ item.studentScore }}%
          </span>
          <template v-else>—</template>
        </template>
        <template #cell(classAvg)="{ item }">
          {{ item.classAvg != null ? item.classAvg + "%" : "—" }}
        </template>
      </eg-pv-table>
    </div>
  </div>
</template>

<script>
import ChartJSLine from "@/components/global/chartjs/chart_js_line.vue";
import MetricsEmptyState from "../shared/metrics_empty_state.vue";
import InfinityLoading from "@/components/rubrics/newRubric/components/infinity-loading.vue";
import EgPvTable from "@/components/global/eg/eg_pv_table/eg_pv_table.vue";
import {
  METRICS_COLOR_STUDENT,
  METRICS_COLOR_CLASS_AVG,
  METRICS_COLOR_STRONG_TEXT,
  METRICS_COLOR_NEED_SUPPORT_TEXT,
  METRICS_GRADE_THRESHOLDS,
} from "@/constants";
import { getScoreTier } from "@/utils/metricsUtils";

const DATASET_COLORS = {
  Student: METRICS_COLOR_STUDENT,
  "Class Avg": METRICS_COLOR_CLASS_AVG,
};

const referenceLinesPlugin = {
  id: "referenceLines",
  afterDraw(chart) {
    const { ctx, chartArea, scales } = chart;
    if (!chartArea) return;

    const lines = [
      {
        value: METRICS_GRADE_THRESHOLDS.HIGH,
        color: METRICS_COLOR_STRONG_TEXT,
        label: `${METRICS_GRADE_THRESHOLDS.HIGH}%`,
      },
      {
        value: METRICS_GRADE_THRESHOLDS.MEDIUM,
        color: METRICS_COLOR_NEED_SUPPORT_TEXT,
        label: `${METRICS_GRADE_THRESHOLDS.MEDIUM}%`,
      },
    ];

    ctx.save();
    lines.forEach(({ value, color, label }) => {
      const y = scales.y.getPixelForValue(value);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
      ctx.setLineDash([]);

      ctx.fillStyle = color;
      ctx.font = "600 11px Inter, sans-serif";
      ctx.textAlign = "left";
      ctx.textBaseline = "middle";
      ctx.fillText(label, chartArea.right + 8, y);
    });
    ctx.restore();
  },
};

const crosshairPlugin = {
  id: "crosshair",
  afterDraw(chart) {
    const activeElements = chart.getActiveElements();
    if (!activeElements.length) return;
    const { ctx, chartArea } = chart;
    const x = activeElements[0].element.x;
    ctx.save();
    ctx.strokeStyle = "#CBD5E1";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(x, chartArea.top);
    ctx.lineTo(x, chartArea.bottom);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.restore();
  },
};

export default {
  name: "MetricsStudentProgressChart",

  components: {
    ChartJSLine,
    MetricsEmptyState,
    InfinityLoading,
    EgPvTable,
  },

  props: {
    chartData: {
      type: Object,
      default: null,
    },
    rows: {
      type: Array,
      default: () => [],
    },
    studentName: {
      type: String,
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    emptyDescription: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      chartPlugins: [referenceLinesPlugin, crosshairPlugin],
      tooltipState: {
        visible: false,
        title: "",
        items: [],
        left: 0,
        top: 0,
      },
    };
  },

  computed: {
    tableFields() {
      return [
        { key: "date", label: this.$t("metrics.date"), style: "width: 130px" },
        { key: "assignmentName", label: this.$t("metrics.assignment") },
        {
          key: "studentScore",
          label: this.$t("metrics.studentScore"),
          style: "width: 140px; text-align: center",
        },
        {
          key: "classAvg",
          label: this.$t("metrics.classAverage"),
          style: "width: 140px; text-align: center",
        },
      ];
    },

    hasData() {
      return !!(
        this.chartData?.labels?.length &&
        this.chartData?.datasets?.some((dataset) =>
          dataset.data?.some((value) => value != null)
        )
      );
    },

    legend() {
      const studentLabel = this.studentName || this.$t("metrics.student");
      return [
        { label: studentLabel, color: METRICS_COLOR_STUDENT },
        { label: this.$t("metrics.classAvg"), color: METRICS_COLOR_CLASS_AVG },
      ];
    },

    chartInput() {
      return {
        labels: this.chartData?.labels || [],
        datasets: this.chartData?.datasets || [],
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: "index",
          intersect: false,
        },
        layout: {
          padding: { top: 8, right: 44 },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            enabled: false,
            external: this.buildTooltip,
          },
        },
        scales: {
          x: {
            grid: { display: false },
            border: { display: false },
            ticks: {
              color: "#566370",
              font: { family: "Inter", size: 12 },
              maxRotation: 0,
              callback: (value) => {
                const label = this.chartInput.labels[value];
                if (!label || label.length <= 28) return label;
                const words = label.split(" ");
                const lines = [];
                let current = "";
                for (const word of words) {
                  const candidate = current ? `${current} ${word}` : word;
                  if (candidate.length > 28) {
                    if (current) lines.push(current);
                    let remaining = word;
                    while (remaining.length > 28) {
                      lines.push(remaining.substring(0, 28));
                      remaining = remaining.substring(28);
                    }
                    current = remaining;
                  } else {
                    current = candidate;
                  }
                }
                if (current) lines.push(current);
                return lines;
              },
            },
          },
          y: {
            min: 0,
            max: 100,
            grid: { color: "#F0F3F6" },
            border: { display: false },
            ticks: {
              color: "#566370",
              font: { family: "Inter", size: 12 },
              callback: (value) => `${value}%`,
              stepSize: 20,
            },
          },
        },
      };
    },

    builtChartData() {
      return {
        labels: this.chartInput.labels,
        datasets: this.chartInput.datasets.map((dataset) => {
          const color =
            DATASET_COLORS[dataset.label] || METRICS_COLOR_CLASS_AVG;
          return {
            label: dataset.label,
            data: dataset.data,
            borderColor: color,
            backgroundColor: color,
            pointBackgroundColor: color,
            pointBorderColor: "#fff",
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            borderWidth: 2,
            tension: 0.3,
            fill: false,
            spanGaps: true,
            clip: false,
          };
        }),
      };
    },
  },

  methods: {
    scoreBadgeClass(score) {
      return `metrics-student-progress-chart__score-badge--${getScoreTier(
        score
      )}`;
    },

    buildTooltip(context) {
      const { chart, tooltip } = context;

      if (tooltip.opacity === 0 || !tooltip.dataPoints?.length) {
        this.tooltipState.visible = false;
        return;
      }

      const canvasRect = chart.canvas.getBoundingClientRect();
      const bodyRect = this.$refs.chartBody?.getBoundingClientRect();
      if (!bodyRect) return;

      const studentLabel = this.studentName || this.$t("metrics.student");
      const classAvgLabel = this.$t("metrics.classAvg");
      const LABEL_MAP = {
        Student: studentLabel,
        "Class Avg": classAvgLabel,
      };

      this.tooltipState = {
        visible: true,
        title: tooltip.title?.[0] || "",
        items: tooltip.dataPoints.map((dataPoint) => ({
          label: LABEL_MAP[dataPoint.dataset.label] || dataPoint.dataset.label,
          value: dataPoint.parsed.y ?? "–",
          color:
            DATASET_COLORS[dataPoint.dataset.label] || METRICS_COLOR_CLASS_AVG,
        })),
        left: canvasRect.left - bodyRect.left + tooltip.caretX,
        top: canvasRect.top - bodyRect.top + tooltip.caretY,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../shared/metrics_card";

.metrics-student-progress-chart {
  display: flex;
  flex-direction: column;
  padding: 24px;
  margin: 0 24px;
  background: var(--color-surface-neutral-background, #fff);
  border: 1px solid var(--color-stroke-neutral-default, #e5ebf0);
  border-radius: 12px;
  gap: 16px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
  }

  &__title {
    font-weight: var(--typography-font-weight-fw-700, 700);
    color: var(--color-text-neutral-on-white, #1a2330);
  }

  &__body {
    position: relative;
    min-height: 260px;
    margin-top: 8px;
  }

  &__legend {
    display: flex;
    align-items: center;
    gap: 16px;
    flex-shrink: 0;
  }

  &__legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__legend-line {
    display: inline-block;
    width: 20px;
    height: 3px;
    border-radius: 2px;
    flex-shrink: 0;
  }

  &__legend-label {
    color: var(--color-text-neutral-fade);
    font-weight: var(--typography-font-weight-fw-600, 600);
    white-space: nowrap;
  }

  &__table-wrap {
    margin-top: 16px;
  }

  &__date {
    color: var(--color-text-neutral-secondary, #566370);
    white-space: nowrap;
    font-size: var(--typography-font-size-fs-200, 12px);
  }

  &__score-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2px 10px;
    border-radius: 4px;
    font-size: var(--typography-font-size-fs-200, 12px);
    font-weight: var(--typography-font-weight-fw-600, 600);
    min-width: 52px;

    &--high {
      background: var(--colors-green-100);
      color: var(--color-text-semantic-onSuccess);
    }

    &--medium {
      background: var(--colors-yellow-200);
      color: var(--colors-yellow-800);
    }

    &--low {
      background: var(--color-surface-semantic-error-light);
      color: var(--color-text-semantic-onError);
    }
  }
}
::v-deep .p-datatable-thead th:nth-child(n + 3) .p-column-header-content {
  justify-content: center;
}
::v-deep .p-datatable-tbody tr td:nth-child(n + 3) {
  text-align: center;
}
.progress-chart-tooltip {
  position: absolute;
  pointer-events: none;
  transform: translate(-50%, calc(-100% - 12px));
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 10px 14px;
  font-family: Inter, sans-serif;
  max-width: 200px;
  z-index: 10;

  &__title {
    margin: 0 0 8px;
    font-weight: var(--typography-font-weight-fw-600, 600);
    color: var(--color-text-neutral-on-white);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 240px;
  }

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;

    &:last-child {
      margin-bottom: 0;
    }
  }

  &__row-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__line {
    flex-shrink: 0;
    width: 20px;
    height: 3px;
    border-radius: 2px;
    display: inline-block;
  }

  &__label {
    color: var(--color-text-neutral-secondary, #566370);
  }

  &__value {
    font-weight: var(--typography-font-weight-fw-600, 600);
    color: var(--color-text-neutral-on-white, #1a2330);
    flex-shrink: 0;
  }
}
</style>
